import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { CommonModule } from '@angular/common';
import { DropDownLink } from 'src/app/models/drop-down-link.interface';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { LocationContextService } from 'src/app/services/location-context.service';
import { ServiceService } from 'src/app/services/service.service';
import { LoginSignupComponent } from '../login-signup/login-signup.component';
import type { Schema } from '../../../../../amplify/data/resource';

type SiteConfigurationSchema = Schema['SiteConfiguration']['type'];


@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: true,
    imports: [
      ReactiveFormsModule,
      RouterModule,
      MatButtonModule,
      MatIconModule,
      MatMenuModule,
      MatDividerModule,
      CommonModule,
      LoginSignupComponent,
    ]
})
export class HeaderComponent implements OnInit, OnDestroy {

  searchbar: boolean = environment.searchBar;
  menuItems: DropDownLink[] = [];
  menuItem!: DropDownLink;
  displayProductsCuracao: boolean = false;
  displayCategoryMenu: boolean = true;
  exitingMenuButton: boolean = true;
  loginSignUp = false;
  loggedOut: boolean = true;
  showCompanyOptions: boolean = false;
  showAdminOptions: boolean = false;
  showUserOptions: boolean = false;
  backOffice: boolean = false;

  userEmailAddress: string | null | undefined;
  permissions: string[] = [];
  userGroups: string[] = [];

  // Site configuration
  siteConfiguration: SiteConfigurationSchema | null = null;
  /** From `SiteConfiguration.logoUrl` only (no bundled asset fallback). */
  logoUrl = '';
  siteName: string = 'Curacao'; // Default name

  range = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  options = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  };

  private authSub?: Subscription;

  constructor(
    private router: Router,
    private authService: AuthService,
    private locationContext: LocationContextService,
    private service: ServiceService
  ) { }

  ngOnInit() {
    this.authSub = this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        void this.userLoggedIn();
      } else {
        this.setLoggedOutState();
      }
    });

    this.loadSiteConfiguration();
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
  }

  /**
   * Load site configuration by current domain/location (falls back to active config if none found)
   */
  loadSiteConfiguration() {
    const location = this.locationContext.location;
    this.service.getSiteConfigurationByLocation(location).subscribe({
      next: (config) => {
        if (config) {
          this.applySiteConfig(config);
          return;
        }
        // Fallback: no config for this location, try active config (for backward compatibility)
        this.service.getActiveSiteConfiguration().subscribe({
          next: (fallbackConfig) => {
            if (fallbackConfig) {
              this.applySiteConfig(fallbackConfig);
            }
          },
          error: () => {}
        });
      },
      error: (error) => {
        console.error('Error loading site configuration:', error);
        this.service.getActiveSiteConfiguration().subscribe({
          next: (fallbackConfig) => {
            if (fallbackConfig) {
              this.applySiteConfig(fallbackConfig);
            }
          },
          error: () => {}
        });
      }
    });
  }

  private applySiteConfig(config: SiteConfigurationSchema) {
    this.siteConfiguration = config;
    this.logoUrl = config.logoUrl?.trim() ?? '';
    if (config.siteName) {
      this.siteName = config.siteName;
      document.title = config.siteName;
    }
    const fav = config.faviconUrl?.trim();
    if (fav) {
      let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = fav;
    }
  }

  async userLoggedIn() {
    try {
      const user = await this.authService.getCurrentUser();
      this.loggedOut = false;
      this.userEmailAddress = user.username;
      await this.authService.processPendingGroupAssignments(user.username);
      this.userGroups = await this.authService.getUserGroupsWithFallback(user.username);
      this.updateNavigationOptions();
    } catch (error) {
      console.log('User is not logged in:', error);
      this.setLoggedOutState();
    }
  }

  private setLoggedOutState(): void {
    this.loggedOut = true;
    this.userGroups = [];
    this.showUserOptions = false;
    this.showCompanyOptions = false;
    this.showAdminOptions = false;
    this.userEmailAddress = null;
  }


  updateNavigationOptions() {
    // Reset all options
    this.showUserOptions = false;
    this.showCompanyOptions = false;
    this.showAdminOptions = false;

    // Set options based on user groups
    // All authenticated users (non-guests) should have access to "My Account"
    this.showUserOptions = true;

    // Show admin options if user is in admin group
    if (this.userGroups.includes('admin')) {
      this.showAdminOptions = true;
    }

    // Show company options if user is in company group
    if (this.userGroups.includes('company')) {
      this.showCompanyOptions = true;
    }

    // If user has both admin and company groups, they'll see all options
    // Admin users automatically get company access, but company users don't get admin access
  }

  exitCarosel(exitingMenu: boolean, exitingCarosel: boolean): void {
    if (exitingMenu && exitingCarosel) {
      this.displayProductsCuracao = false;
    }
  }

  loginBoxShow(): void {
    this.loginSignUp = true;
  }

  loginBoxHide(): void {
    this.loginSignUp = false;
    void this.userLoggedIn();
  }

  async logout(): Promise<void> {
    try {
      await this.authService.logout();
      this.setLoggedOutState();
      await this.router.navigate(['/']);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }
}

